/**
 * <PackagesRenewalRisk /> — renewal risk widget for Packages Dashboard.
 *
 * Fetches /workspaces/{ws}/packages/renewal-risk and shows the list with
 * risk badge + client contact hint.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtMoney } from "@/pages/app/Packages";
import { AlertTriangle, PhoneCall, CircleAlert, ShieldCheck } from "lucide-react";

const RISK_TONES = {
  high: "border-red-500/40 text-red-700 bg-red-500/5",
  medium: "border-amber-500/40 text-amber-700 bg-amber-500/5",
  low: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
};

const RISK_ICONS = {
  high: CircleAlert,
  medium: AlertTriangle,
  low: ShieldCheck,
};

export default function PackagesRenewalRisk({ wsId, days = 30 }) {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = auth.activeWorkspace?.currency || "INR";
  const ws = wsId || auth.activeWorkspaceId;

  useEffect(() => {
    if (!ws) return;
    let alive = true;
    setLoading(true);
    api.get(`/workspaces/${ws}/packages/renewal-risk?days=${days}`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ws, days]);

  if (loading) return <div className="text-sm text-muted-foreground py-4">Checking renewals…</div>;
  if (!data) return null;
  const t = data.totals || {};
  const pkgs = data.packages || [];

  return (
    <div className="border border-border p-6 space-y-4" data-testid="packages-renewal-risk">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Renewal risk (next {days} days)</div>
          <p className="text-xs text-muted-foreground mt-1">
            Active/paused packages expiring soon. Risk considers days-to-expiry AND whether the client has been in touch in the last 14 days.
          </p>
        </div>
        {data.can_view_rates && t.arr_at_risk > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">ARR at risk</div>
            <div className="text-lg font-mono font-bold">{fmtMoney(t.arr_at_risk, currency)}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {["high", "medium", "low"].map((r) => (
          <div key={r} className={`p-3 border text-center ${RISK_TONES[r]}`} data-testid={`renewal-count-${r}`}>
            <div className="text-lg font-mono font-bold">{t[r] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5">{r} risk</div>
          </div>
        ))}
      </div>

      {pkgs.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">Nothing expiring in the next {days} days. Nice.</div>
      ) : (
        <div className="space-y-1" data-testid="renewal-package-list">
          {pkgs.slice(0, 12).map((p) => {
            const Icon = RISK_ICONS[p.risk] || CircleAlert;
            return (
              <Link
                key={p.id}
                to={`/app/packages/${p.id}`}
                className="flex items-center gap-3 border border-border/60 hover:border-primary/50 p-3 transition group"
                data-testid={`renewal-pkg-${p.id}`}
              >
                <Icon className={`w-3.5 h-3.5 shrink-0 ${p.risk === "high" ? "text-red-600" : p.risk === "medium" ? "text-amber-600" : "text-emerald-600"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate group-hover:text-primary transition">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {p.client_name} · {p.brand_name} · expires {p.end_date}
                  </div>
                </div>
                <div className="text-right shrink-0 flex items-center gap-3">
                  {!p.has_recent_contact && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-700" title="No client contact in last 14 days">
                      <PhoneCall className="w-3 h-3" /> quiet
                    </span>
                  )}
                  <span className="text-xs font-mono">
                    <span className={`font-bold ${p.days_to_expiry <= 7 ? "text-red-600" : p.days_to_expiry <= 30 ? "text-amber-600" : ""}`}>
                      {p.days_to_expiry < 0 ? `${Math.abs(p.days_to_expiry)}d ago` : `${p.days_to_expiry}d`}
                    </span>
                  </span>
                  {p.monthly_price != null && (
                    <span className="text-xs font-mono min-w-[80px] text-right">{fmtMoney(p.monthly_price, currency)}</span>
                  )}
                </div>
              </Link>
            );
          })}
          {pkgs.length > 12 && (
            <div className="text-[11px] text-muted-foreground pt-1 text-center">
              …and {pkgs.length - 12} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}
